import React, { useState, useEffect } from 'react';

function ProgressBar({ 
  progress, 
  files = [], 
  currentFileIndex = 0, 
  currentFileName = '', 
  totalFiles = 1,
  onCancel,
  uploadSpeed = 0,
  remainingTime = 0,
  bytesUploaded = 0,
  totalBytes = 0,
  destination = 'Cloud Storage',
  mode = 'upload' // 'upload' or 'download'
}) {
  const [startTime] = useState(Date.now());
  const [averageSpeed, setAverageSpeed] = useState(0);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle mobile detection and resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate real-time statistics
  useEffect(() => {
    const elapsed = (Date.now() - startTime) / 1000; // seconds
    if (elapsed > 0 && bytesUploaded > 0) {
      const avgSpeed = bytesUploaded / elapsed; // bytes per second
      setAverageSpeed(avgSpeed);
      
      const remaining = totalBytes - bytesUploaded;
      if (avgSpeed > 0) {
        setEstimatedTimeRemaining(remaining / avgSpeed);
      }
    }
  }, [bytesUploaded, totalBytes, startTime]);

  // Professional SVG Icons
  const FolderIcon = () => (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
      <path d="M9.828 3h3.982a2 2 0 0 1 1.992 2.181l-.637 7A2 2 0 0 1 13.174 14H2.825a2 2 0 0 1-1.991-1.819l-.637-7a1.99 1.99 0 0 1 .342-1.31L.5 3a2 2 0 0 1 2-2h3.672a2 2 0 0 1 1.414.586l.828.828A2 2 0 0 0 9.828 3zm-8.322.12C1.72 3.042 1.95 3 2.19 3h5.396l-.707-.707A1 1 0 0 0 6.172 2H2.5a1 1 0 0 0-1 .981l.006.139z"/>
    </svg>
  );

  const QueueIcon = () => (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
      <path fillRule="evenodd" d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5z"/>
    </svg>
  );

  const SpeedIcon = () => (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 2a.5.5 0 0 1 .5.5V4a.5.5 0 0 1-1 0V2.5A.5.5 0 0 1 8 2zM3.732 3.732a.5.5 0 0 1 .707 0l.915.914a.5.5 0 1 1-.708.708l-.914-.915a.5.5 0 0 1 0-.707zM2 8a.5.5 0 0 1 .5-.5h1.586a.5.5 0 0 1 0 1H2.5A.5.5 0 0 1 2 8zm9.5 0a.5.5 0 0 1 .5-.5h1.5a.5.5 0 0 1 0 1H12a.5.5 0 0 1-.5-.5zm.754-4.246a.389.389 0 0 0-.527-.02L7.547 7.31A.91.91 0 1 0 8.85 8.569l3.434-4.297a.389.389 0 0 0-.029-.518z"/>
      <path fillRule="evenodd" d="M6.664 15.889A8 8 0 1 1 9.336.11a8 8 0 0 1-2.672 15.78zm-4.665-4.283A11.945 11.945 0 0 1 8 10c2.186 0 4.236.585 6.001 1.606a7 7 0 1 0-12.002 0z"/>
    </svg>
  );

  const ClockIcon = () => (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
      <path d="M7.5 3a.5.5 0 0 1 .5.5v5.21l3.248 1.856a.5.5 0 0 1-.496.868l-3.5-2A.5.5 0 0 1 7 9V3.5a.5.5 0 0 1 .5-.5z"/>
    </svg>
  );

  const ProgressIcon = () => (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
      <path d="M10.067.87a2.89 2.89 0 0 0-4.134 0l-.622.638-.89-.011a2.89 2.89 0 0 0-2.924 2.924l.01.89-.636.622a2.89 2.89 0 0 0 0 4.134l.637.622-.011.89a2.89 2.89 0 0 0 2.924 2.924l.89-.01.622.636a2.89 2.89 0 0 0 4.134 0l.622-.637.89.011a2.89 2.89 0 0 0 2.924-2.924l-.01-.89.636-.622a2.89 2.89 0 0 0 0-4.134l-.637-.622.011-.89a2.89 2.89 0 0 0-2.924-2.924l-.89.01-.622-.636zm.287 5.984-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7 8.793l2.646-2.647a.5.5 0 0 1 .708.708z"/>
    </svg>
  );

  const FileIcon = () => (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
      <path d="M9.5 1H3a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V4.5L9.5 1ZM3 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4.293a1 1 0 0 0-.293-.707l-4-4A1 1 0 0 0 10 3H3Z"/>
    </svg>
  );

  const DownloadIcon = () => (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
      <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
      <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
    </svg>
  );

  // Format speed (bytes/second to MB/s)
  const formatSpeed = (bytesPerSecond) => {
    const mbps = bytesPerSecond / (1024 * 1024);
    return `${mbps.toFixed(1)} MB/s`;
  };

  // Format time (seconds to HH:MM:SS) with non-negative clamp
  const formatTime = (seconds) => {
    if (!seconds || !isFinite(seconds)) return '00:00:00';
    const safe = Math.max(0, seconds);
    const hours = Math.floor(safe / 3600);
    const minutes = Math.floor((safe % 3600) / 60);
    const secs = Math.floor(safe % 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Get current file details
  const getCurrentFile = () => {
    if (files.length > 0 && currentFileIndex < files.length) {
      return files[currentFileIndex];
    }
    return { name: currentFileName, size: 0 };
  };

  const currentFile = getCurrentFile();
  const queueCount = Math.max(totalFiles - currentFileIndex, 1);
  const isDownload = mode === 'download';
  const progressColor = 'var(--primary)';
  const safeProgress = Math.max(0, Math.min(100, Math.round(progress || 0)));
  const leftRadius = (safeProgress <= 0.5) ? '0px' : '3px';
  const rightRadius = (safeProgress >= 99.5) ? '3px' : '0px';

  return (
    <div>
      {/* Upload/Download Statistics Header */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: isMobile ? '6px' : '12px',
        marginBottom: isMobile ? '8px' : '12px',
        fontSize: isMobile ? '9px' : '10px',
        color: '#9ca3af'
      }}>
        {/* On mobile, show only essential stats */}
        {!isMobile && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {isDownload ? <DownloadIcon /> : <FolderIcon />}
          <span>{isDownload ? 'Source: ' : 'Destination: '}</span>
          <span style={{ color: '#e2e8f0' }}>{destination}</span>
        </div>
        )}
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <QueueIcon />
          <span>{isMobile ? '' : 'Queue: '}</span>
          <span style={{ color: '#e2e8f0' }}>{queueCount} files</span>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <SpeedIcon />
          <span>{isMobile ? '' : 'Speed: '}</span>
          <span style={{ color: '#60a5fa' }}>{formatSpeed(averageSpeed || uploadSpeed)}</span>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ClockIcon />
          <span>{isMobile ? '' : 'Remaining: '}</span>
          <span style={{ color: '#f59e0b' }}>{formatTime(estimatedTimeRemaining || remainingTime)}</span>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ProgressIcon />
          <span>{isMobile ? '' : 'Progress: '}</span>
          <span style={{ color: progressColor }}>{safeProgress}%</span>
        </div>
      </div>

      {/* Current File Progress */}
      <div style={{
        backgroundColor: '#181a22',
        border: '1px solid #242834',
        borderRadius: '6px',
        padding: isMobile ? '8px' : '12px',
        marginBottom: '8px'
      }}>
        {/* File Header */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: isMobile ? 'flex-start' : 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          marginBottom: '8px',
          gap: isMobile ? '6px' : '0'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px',
            minWidth: 0, // Allow shrinking
            flex: isMobile ? 'none' : '1',
            maxWidth: '100%'
          }}>
            <FileIcon />
            <span style={{ 
              fontSize: isMobile ? '10px' : '11px', 
              fontWeight: '500',
              color: '#e2e8f0',
              maxWidth: isMobile ? 'calc(100vw - 120px)' : '300px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {currentFile.name || currentFileName || (isDownload ? 'Downloading...' : 'Uploading...')}
            </span>
          </div>
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isMobile ? '8px' : '12px', 
            fontSize: isMobile ? '9px' : '10px',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            justifyContent: isMobile ? 'flex-start' : 'flex-end'
          }}>
            <span style={{ color: '#9ca3af' }}>
              {formatFileSize(currentFile.size || totalBytes)}
            </span>
            {!isMobile && (
            <span style={{ color: '#60a5fa' }}>
              {formatSpeed(uploadSpeed || averageSpeed)}
            </span>
            )}
            {!isMobile && (
            <span style={{ color: '#f59e0b' }}>
              {formatTime(remainingTime || estimatedTimeRemaining)}
            </span>
            )}
            <span style={{ color: progressColor, fontWeight: '600' }}>
              {safeProgress}%
            </span>
          </div>
        </div>

        {/* Progress Bar */}
        <div style={{
          width: '100%',
          height: '6px',
          backgroundColor: '#20242f',
          borderRadius: '3px',
          overflow: 'hidden'
        }}>
          <div 
            style={{
              height: '100%',
              background: isDownload 
                ? 'linear-gradient(90deg, #2563eb 0%, #3b82f6 100%)'
                : 'linear-gradient(90deg, #059669 0%, #10b981 100%)',
              width: `${safeProgress}%`,
              transition: 'width 0.2s ease-out',
              borderTopLeftRadius: leftRadius,
              borderBottomLeftRadius: leftRadius,
              borderTopRightRadius: rightRadius,
              borderBottomRightRadius: rightRadius,
              position: 'relative'
            }}
          >
            {/* Animated barber-pole stripes */}
            {safeProgress > 0 && safeProgress < 100 && (
              <div style={{
                position: 'absolute',
                inset: 0,
                borderTopLeftRadius: leftRadius,
                borderBottomLeftRadius: leftRadius,
                borderTopRightRadius: rightRadius,
                borderBottomRightRadius: rightRadius,
                backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.18) 0 10px, rgba(255,255,255,0.06) 10px 20px)',
                backgroundSize: '20px 20px',
                backgroundPosition: '0 0',
                animation: 'barber 1s linear infinite',
                opacity: 0.6,
                pointerEvents: 'none'
              }} />
            )}
          </div>
        </div>
      </div>

      {/* Queue Information */}
      {totalFiles > 1 && (
        <div style={{
          fontSize: isMobile ? '9px' : '10px',
          color: '#9ca3af',
          marginBottom: '8px',
          textAlign: isMobile ? 'center' : 'left'
        }}>
          {isDownload ? 'Downloading' : 'Processing'} file {currentFileIndex + 1} of {totalFiles}
          {queueCount > 1 && !isMobile && ` • ${queueCount - 1} more files in queue`}
          {queueCount > 1 && isMobile && (
            <div style={{ marginTop: '2px' }}>
              {queueCount - 1} more files in queue
            </div>
          )}
        </div>
      )}

      {/* Cancel Button */}
      {onCancel && (
        <div style={{ 
          display: 'flex', 
          justifyContent: isMobile ? 'center' : 'flex-end', 
          marginTop: '8px' 
        }}>
          <button
            onClick={onCancel}
            style={{
              backgroundColor: '#dc2626',
              color: '#fff',
              border: '1px solid #dc2626',
              borderRadius: '2px',
              padding: isMobile ? '6px 12px' : '4px 8px',
              fontSize: isMobile ? '11px' : '10px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              minWidth: isMobile ? '120px' : 'auto'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#b91c1c'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#dc2626'}
          >
            {isDownload ? 'Cancel Download' : 'Cancel Upload'}
          </button>
        </div>
      )}

      {/* CSS for animation */}
      <style>{`
        @keyframes barber {
          0% { background-position: 0 0; }
          100% { background-position: 40px 0; }
        }
      `}</style>
    </div>
  );
}

export default ProgressBar; 